import { ReactElement } from 'react';
import { useParams } from 'react-router-dom';
import { useSetPageTitle } from 'src/hooks/useSetPageTitle';
import { useGetSurveyGroupSharedResultsQuery } from 'src/redux/survey/survey-groups.endpoints';
import { Skeleton } from 'src/components/ui/skeleton';

/**
 * Public (unauthenticated) view for shared survey group results.
 * Payloads are per survey; compare or aggregate in higher-level UI later.
 */
const SurveyGroupShareResultsPage = (): ReactElement => {
  useSetPageTitle('Survey group results');
  const { shareId } = useParams<{ shareId?: string }>();
  const { data, isLoading, isError } = useGetSurveyGroupSharedResultsQuery(
    { shareId: shareId ?? '' },
    { skip: !shareId }
  );

  if (!shareId) {
    return (
      <div className="p-8 text-sm text-gray-600">
        Missing share link.
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="p-8 space-y-4 max-w-2xl">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-3/4" />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="p-8 text-sm text-gray-600">
        Unable to load survey group results.
      </div>
    );
  }

  return (
    <div className="p-8 max-w-3xl space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">
          Survey group results
        </h1>
        <p className="mt-1 text-sm text-gray-500">
          {data.surveys.length}
          {' '}
          survey
          {data.surveys.length === 1 ? '' : 's'}
          {' '}
          in this group. Compare metrics across surveys using the data below (per-survey payloads match single-survey reporting).
        </p>
      </div>
      <ul className="space-y-3">
        {data.surveys.map((s) => (
          <li
            key={s.ext_survey_id}
            className="rounded-lg border border-gray-200 bg-white px-4 py-3 shadow-sm"
          >
            <p className="font-medium text-gray-900">
              {s.title}
            </p>
            <p className="text-xs text-gray-500 mt-0.5">
              {s.ext_survey_id}
            </p>
            <dl className="mt-2 flex flex-wrap gap-3 text-xs text-gray-600">
              <span>
                Stats:
                {' '}
                {s.stats !== undefined ? 'yes' : '—'}
              </span>
              <span>
                Trends:
                {' '}
                {s.trends !== undefined ? 'yes' : '—'}
              </span>
              <span>
                Summary:
                {' '}
                {s.summary !== undefined ? 'yes' : '—'}
              </span>
            </dl>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default SurveyGroupShareResultsPage;
